import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useState } from "react";
import { useLocale } from "../i18n/LocaleContext";
import { BrandLogo } from "./BrandLogo";
import { buildLocalizedPath } from "../lib/siteRouting";

export function Header() {
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { locale, setLocale, copy, locales } = useLocale();

  const navItems = copy.header.nav.filter((item) =>
    ["#home", "#brands", "#about", "#ai-solutions", "#network", "#contact"].includes(
      item.href
    )
  );
  const localeList = Object.entries(locales) as Array<[keyof typeof locales, (typeof locales)[keyof typeof locales]]>;

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 24);
  });

  return (
    <motion.header
      style={{
        backgroundColor: isScrolled ? "rgba(247,251,253,0.78)" : "rgba(255,255,255,0)",
        borderColor: isScrolled ? "rgba(152,176,188,0.18)" : "rgba(255,255,255,0)",
      }}
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        isScrolled ? "backdrop-blur-xl" : ""
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8 sm:py-3.5 lg:px-12">
        <a href="#home" className="flex items-center gap-4" onClick={() => setMenuOpen(false)}>
          <BrandLogo size="sm" className="shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-[1.02rem] font-semibold tracking-[0.18em] text-[#1f2b31] sm:text-[1.08rem]">
              {copy.header.companyName}
            </div>
            <div className="text-[9px] font-light tracking-[0.34em] text-aurora/62">{copy.header.companyCn}</div>
          </div>
        </a>

        <div className="hidden items-center gap-5 md:flex">
          <nav className="flex items-center gap-4 lg:gap-5">
            {navItems.map((item) => (
              <motion.a
                key={item.href}
                href={item.href}
                whileHover="hover"
                initial="rest"
                animate="rest"
                className="group relative py-2 text-[9px] font-medium uppercase tracking-[0.28em] text-[#6a777c] lg:text-[9px]"
              >
                <span className="transition-colors duration-300 group-hover:text-[#1f2b31]">{item.label}</span>
                <motion.span
                  variants={{ rest: { scaleX: 0 }, hover: { scaleX: 1 } }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="absolute inset-x-0 -bottom-0.5 h-px origin-left bg-aurora"
                />
              </motion.a>
            ))}
            <a
              href={buildLocalizedPath(locale, "/company/verification")}
              className="py-2 text-[9px] font-medium uppercase tracking-[0.22em] text-[#6a777c] transition hover:text-[#1f2b31]"
            >
              {locale === "zh" ? "企业核验" : locale === "ko" ? "기업 확인" : locale === "vi" ? "Xác minh" : "Verify"}
            </a>
          </nav>
          <div className="flex items-center gap-1 rounded-full border border-[#d8e5ec] bg-[rgba(250,253,255,0.78)] p-1 text-[8px] uppercase tracking-[0.28em] text-[#6b787c] shadow-[0_8px_18px_rgba(96,128,142,0.05)] backdrop-blur">
            {localeList.map(([key, value]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLocale(key as any)}
                className={`rounded-full px-2.5 py-1.5 transition ${
                  locale === key ? "bg-aurora/65 text-[#1f2b31]" : "hover:bg-[#eef5f8]"
                }`}
              >
                {value.code}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setMenuOpen((current) => !current)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#d8e5ec] bg-[rgba(250,253,255,0.86)] text-[#1f2b31] shadow-[0_10px_20px_rgba(96,128,142,0.06)] backdrop-blur md:hidden"
        >
          <span className="relative h-4 w-5">
            <span
              className={`absolute left-0 top-0 h-0.5 w-5 bg-[#1f2b31] transition ${menuOpen ? "translate-y-[7px] rotate-45" : ""}`}
            />
            <span
              className={`absolute left-0 top-[7px] h-0.5 w-5 bg-[#1f2b31] transition ${menuOpen ? "opacity-0" : "opacity-100"}`}
            />
            <span
              className={`absolute left-0 top-[14px] h-0.5 w-5 bg-[#1f2b31] transition ${menuOpen ? "-translate-y-[7px] -rotate-45" : ""}`}
            />
          </span>
        </button>
      </div>

      <motion.div
        initial={false}
        animate={{
          height: menuOpen ? "auto" : 0,
          opacity: menuOpen ? 1 : 0,
        }}
        className="overflow-hidden border-t border-[#d8e5ec] bg-[rgba(246,250,252,0.95)] backdrop-blur-xl md:hidden"
      >
        <nav className="mx-auto flex max-w-7xl flex-col px-5 py-4 sm:px-8">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              className="border-b border-[#e2edf2] py-4 text-sm tracking-[0.18em] text-[#69767a] transition hover:text-[#1f2b31]"
            >
              {item.label}
            </a>
          ))}
          <a
            href={buildLocalizedPath(locale, "/company/verification")}
            onClick={() => setMenuOpen(false)}
            className="border-b border-[#e2edf2] py-4 text-sm tracking-[0.18em] text-[#69767a] transition hover:text-[#1f2b31]"
          >
            {locale === "zh" ? "企业核验" : locale === "ko" ? "기업 정보 확인" : locale === "vi" ? "Xác minh doanh nghiệp" : "Company Verification"}
          </a>
          <div className="mt-4 flex flex-wrap gap-2">
            {localeList.map(([key, value]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setLocale(key as any);
                  setMenuOpen(false);
                }}
                className={`rounded-full border px-3 py-2 text-[10px] uppercase tracking-[0.22em] ${
                  locale === key
                    ? "border-aurora/35 bg-aurora/12 text-[#1f2b31]"
                    : "border-[#d8e5ec] bg-[rgba(250,253,255,0.84)] text-[#69767a]"
                }`}
              >
                {value.nativeLabel}
              </button>
            ))}
          </div>
        </nav>
      </motion.div>
    </motion.header>
  );
}
